// StatusTriggersHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import BoltIcon from "@mui/icons-material/Bolt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import LayersIcon from "@mui/icons-material/Layers";
import RuleIcon from "@mui/icons-material/Rule";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };
type StatusValue = { status: string; subStatuses: string[] };

const DEFAULT_PDFS = [
  {
    title: "Status & Triggers Manual",
    href: "https://mdimages-test.s3.amazonaws.com/dc1cdcd1-2288-4ce7-98e3-5cb2d76948ba_Status_and_Triggers_Manual_Improved.pdf",
  },
];

// ---- Static Content ----

const STATUS_VS_DROPDOWN = [
  { feature: "Tracks lead lifecycle", status: "✅", dropdown: "❌" },
  { feature: "Full audit log (who, when)", status: "✅", dropdown: "❌" },
  { feature: "Automated triggers on change", status: "✅", dropdown: "❌" },
  { feature: "Predefined list of values", status: "✅", dropdown: "✅" },
];

const STATUS_HIERARCHY_EXAMPLE: StatusValue[] = [
  { status: "Academics", subStatuses: ["Arun", "Aayush", "Aman"] },
  { status: "Hostel", subStatuses: ["Shahid", "Rahul", "Deepak"] },
  { status: "Sports", subStatuses: ["Vaibhav", "Utkarsh"] },
];

const TRIGGER_TYPES = [
  {
    title: "WhatsApp Trigger",
    icon: <WhatsAppIcon />,
    desc: "Sends a pre-approved WhatsApp template to a customer or internal user whenever the attached Status or Sub-Status is updated.",
    bullets: [
      "Recipient is notified automatically — no manual sending required",
      "Can send to End Customer or internal Profile/Executive",
      "Template must be pre-approved",
    ],
  },
  {
    title: "Assign a Lead Trigger",
    icon: <AssignmentIcon />,
    desc: "Automatically assigns the lead to a chosen profile and user when the attached Status or Sub-Status is updated.",
    bullets: [
      "Removes need for manual lead routing",
      "Select Profile and specific Users under that profile",
      "Users must be assigned to the Business Service to appear",
    ],
  },
];

const CREATE_STATUS_STEPS: StepBlock[] = [
  { title: "Open the Webform", bullets: ["Go to the webform attached to the Business Service / Lead Dashboard."] },
  { title: "Add a New Field", bullets: ["Name it after the column under which statuses will live — e.g., 'Assign to Department'."] },
  { title: "Set Field Type to Dropdown", bullets: ["Select Dropdown as the Field Type.", "Clear out any default dropdown values."] },
  { title: "Mark as Status or Sub-Status", bullets: ["Select either the Status or Sub-Status option for this field."] },
  { title: "Submit the Form", bullets: ["Click Submit to save the field configuration."] },
];

const ADD_STATUS_VALUES_STEPS: StepBlock[] = [
  { title: "Click Create", bullets: ["Begin adding actual status values for this column."] },
  { title: "Select the Status Column", bullets: ["In Type of Status, select the column you created."] },
  { title: "Type the Status Name", bullets: ["e.g., 'Academics'."] },
  { title: "Mark it Active", bullets: ["Set the status as Active."] },
];

const ATTACH_TRIGGER_STEPS: StepBlock[] = [
  { title: "Click Add Trigger", bullets: ["On the status value you just created, click Add Trigger."] },
  { title: "Select Trigger Type", bullets: ["Choose WhatsApp or Assign a Lead."] },
  { title: "Configure WhatsApp", bullets: ["Select Organisation, Bot Number, and Template.", "Choose recipient: End Customer or Profile/Executive."] },
  { title: "Configure Assign Lead", bullets: ["Select Profile and specific Users.", "Users appear only if assigned to Business Service."] },
  { title: "Add More Triggers", bullets: ["Any number of triggers can be attached to a single status value."] },
];

const ADD_SUB_STATUS_STEPS: StepBlock[] = [
  { title: "Click Add Sub Status", bullets: ["Click Add Sub Status."] },
  { title: "Select Sub-Status Type", bullets: ["Select the Sub-Status type field declared earlier."] },
  { title: "Type Sub-Status Name", bullets: ["Enter the name of the Sub-Status."] },
  { title: "Configure Triggers", bullets: ["Follow the same process as attaching triggers to a Status."] },
];

const PARAMETER_MAPPING_EXAMPLE = [
  { parameter: "{{1}}", field: "Parent Name" },
  { parameter: "{{2}}", field: "lead_id" },
  { parameter: "{{3}}", field: "Status" },
  { parameter: "{{4}}", field: "Student Name" },
  { parameter: "{{5}}", field: "Grievance Category" },
];

const SAMPLE_TEMPLATE = "Hi {{1}}, Your Grievance Ticket: {{2}} status has updated to {{3}}. Student Name: {{4}}. Category: {{5}}";
const SAMPLE_RESULT = '"Hi Mrs. Sharma, Your Grievance Ticket: GR1042 status has updated to Resolved. Student Name: Aanya Sharma. Category: Hostel"';

const Pill: React.FC<{ label: string; color?: string }> = ({ label, color }) => (
  <Chip
    size="small"
    label={label}
    sx={{ borderRadius: 2, ...(color ? { bgcolor: `${color}20`, color, fontWeight: 600 } : {}) }}
  />
);

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 3 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const StatusTriggersHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");
    
    return {
      overview: "status triggers track lead lifecycle automated action whatsapp notification lead assignment status change audit log who when",
      status: "status dropdown field track manage lead lifecycle logging who made change when triggers attached to status value automated action",
      subStatus: "sub-status nested under parent status dependent values distinct options triggers attached",
      triggers: "whatsapp trigger assign lead trigger automated action fires when status updated pre-approved template lead assignment",
      createStatus: flatten(CREATE_STATUS_STEPS) + " webform dropdown field status sub-status",
      addValues: flatten(ADD_STATUS_VALUES_STEPS) + " active inactive status values",
      attachTrigger: flatten(ATTACH_TRIGGER_STEPS) + " whatsapp template profile executive user assignment",
      addSubStatus: flatten(ADD_SUB_STATUS_STEPS) + " nested sub-status",
      parameterMapping: "parameter mapping dashboard field name case-sensitive template variables {{1}} {{2}} match exactly",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="Status & Triggers Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: 560,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TrackChangesIcon />
              <Typography variant="h6" fontWeight={800}>
                Status & Triggers • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search status, triggers, sub-status, mapping…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{ mb: 1 }} />
              <Chip label="Status" size="small" variant="outlined" onClick={() => jumpTo("#sec-status")} sx={{ mb: 1 }} />
              <Chip label="Sub-Status" size="small" variant="outlined" onClick={() => jumpTo("#sec-substatus")} sx={{ mb: 1 }} />
              <Chip label="Triggers" size="small" variant="outlined" onClick={() => jumpTo("#sec-triggers")} sx={{ mb: 1 }} />
              <Chip label="Create Status" size="small" variant="outlined" onClick={() => jumpTo("#sec-create-status")} sx={{ mb: 1 }} />
              <Chip label="Add Values" size="small" variant="outlined" onClick={() => jumpTo("#sec-add-values")} sx={{ mb: 1 }} />
              <Chip label="Attach Trigger" size="small" variant="outlined" onClick={() => jumpTo("#sec-attach-trigger")} sx={{ mb: 1 }} />
              <Chip label="Parameter Mapping" size="small" variant="outlined" onClick={() => jumpTo("#sec-parameter-mapping")} sx={{ mb: 1 }} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Manage and automate the lifecycle of a lead." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Status and Triggers are two connected features used to manage and automate the lifecycle of a lead or record.
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>Status</Typography>
                        <Typography variant="body2">Tracks the stage of a lead. Fully auditable.</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>Trigger</Typography>
                        <Typography variant="body2">Automated action fired when Status updates.</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Card>
              </>
            )}

            {/* Status */}
            {matches("status") && (
              <>
                <SectionHeader id="sec-status" title="What Is a Status?" subtitle="A special Dropdown field with tracking and triggers." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    A Status is a Dropdown field with two capabilities a plain Dropdown does not have:
                  </Typography>
                  <List dense sx={{ pt: 0 }}>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Tracking — every change is logged, recording who and when." />
                    </ListItem>
                    <ListItem sx={{ py: 0.2 }}>
                      <ListItemText primaryTypographyProps={{ variant: "body2" }} primary="• Triggers — automated actions fire when a status value is selected." />
                    </ListItem>
                  </List>
                  
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Status vs. Standard Dropdown</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Feature</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Dropdown</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {STATUS_VS_DROPDOWN.map((row) => (
                          <TableRow key={row.feature}>
                            <TableCell>{row.feature}</TableCell>
                            <TableCell>{row.status}</TableCell>
                            <TableCell>{row.dropdown}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </>
            )}

            {/* Sub-Status */}
            {matches("subStatus") && (
              <>
                <SectionHeader id="sec-substatus" title="What Is a Sub-Status?" subtitle="Nested under a parent Status." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    A Sub-Status is nested under a parent Status. Available values depend on which Status is selected.
                  </Typography>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    <strong>Key Rule:</strong> Sub-Status values are dependent on the Status value selected.
                  </Alert>
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Example: Assign to Department</Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "action.hover" }}>
                          <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Sub-Status Options</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {STATUS_HIERARCHY_EXAMPLE.map((row) => (
                          <TableRow key={row.status}>
                            <TableCell fontWeight={600}>{row.status}</TableCell>
                            <TableCell>{row.subStatuses.join(", ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Selecting 'Academics' as Status will only show Arun, Aayush, and Aman — not Shahid, Rahul, or Deepak.
                  </Alert>
                </Card>
              </>
            )}

            {/* Triggers */}
            {matches("triggers") && (
              <>
                <SectionHeader id="sec-triggers" title="Trigger Types" subtitle="Automated actions fired on Status or Sub-Status update." />
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  {TRIGGER_TYPES.map((t) => (
                    <Grid item xs={12} key={t.title}>
                      <Card variant="outlined" sx={{ borderRadius: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            px: 2,
                            py: 1,
                            borderBottom: (t) => `1px solid ${t.palette.divider}`,
                            bgcolor: "action.hover",
                          }}
                        >
                          {t.icon}
                          <Typography fontWeight={700}>{t.title}</Typography>
                        </Box>
                        <CardContent sx={{ pt: 1.25 }}>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {t.desc}
                          </Typography>
                          <List dense sx={{ pt: 0 }}>
                            {t.bullets.map((b, i) => (
                              <ListItem key={i} sx={{ py: 0.2 }}>
                                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}

            {/* Create Status */}
            {matches("createStatus") && (
              <>
                <SectionHeader id="sec-create-status" title="Creating a Status" subtitle="Set up a new Status on a webform." />
                <StepAccordion steps={CREATE_STATUS_STEPS} label="Create the Status Field" defaultExpanded />
              </>
            )}

            {/* Add Values */}
            {matches("addValues") && (
              <>
                <SectionHeader id="sec-add-values" title="Add Status & Sub-Status Values" />
                <StepAccordion steps={ADD_STATUS_VALUES_STEPS} label="Add Values" />
              </>
            )}

            {/* Attach Trigger */}
            {matches("attachTrigger") && (
              <>
                <SectionHeader id="sec-attach-trigger" title="Attach a Trigger to the Status" />
                <StepAccordion steps={ATTACH_TRIGGER_STEPS} label="Attach Trigger" defaultExpanded />
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>User Visibility Rule:</strong> An executive will only appear in the Assign a Lead dropdown if they have already been assigned to this Business Service.
                </Alert>
              </>
            )}

            {/* Add Sub-Status */}
            {matches("addSubStatus") && (
              <>
                <SectionHeader title="Add a Sub-Status" />
                <StepAccordion steps={ADD_SUB_STATUS_STEPS} label="Add Sub-Status" />
              </>
            )}

            {/* Parameter Mapping */}
            {matches("parameterMapping") && (
              <>
                <SectionHeader id="sec-parameter-mapping" title="Template Parameter Mapping" subtitle="Map dashboard fields to template variables." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    WhatsApp template triggers pull live data from the lead dashboard using positional parameters — {'{{1}}'}, {'{{2}}'}, {'{{3}}'}, etc.
                  </Typography>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <strong>Matching Rule:</strong> Parameter mapping must exactly match the Field Name in the Lead Dashboard. Matching is case-sensitive.
                  </Alert>
                  
                  <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Example: Grievance Ticket Notification</Typography>
                  <Card variant="outlined" sx={{ p: 1.5, mt: 1, bgcolor: "action.hover", borderRadius: 2 }}>
                    <Typography variant="body2" fontWeight={600}>Template:</Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic", mb: 1 }}>"{SAMPLE_TEMPLATE}"</Typography>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: "action.hover" }}>
                            <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Mapped Dashboard Field</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {PARAMETER_MAPPING_EXAMPLE.map((row) => (
                            <TableRow key={row.parameter}>
                              <TableCell>{row.parameter}</TableCell>
                              <TableCell>{row.field}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Result:</Typography>
                    <Typography variant="body2" sx={{ fontStyle: "italic" }}>{SAMPLE_RESULT}</Typography>
                  </Card>
                </Card>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default StatusTriggersHelp;